/**
 * InGo's own vehicles, drawn from above (Rio, 2 Oct 2026: "i think you can
 * design one according to the vehicle"; InGo ADR 0036, the live-map plan):
 * a motorbike carrying the orange InGo box, a tuk-tuk, and a car, each
 * pointing north, so turning one to a heading is a single rotation.
 *
 * On a map each sits on a white disc whose ring says its state in colour:
 * green free, blue on a delivery, grey not reporting (Kangaru's live map:
 * pale ground, so the grey of a silent vehicle still shows). The words for
 * each state are the screen's job, never the colour's alone.
 *
 * This module is the one source of the drawings: `public/assets/vehicles/`
 * holds the same three as files for the office's console to copy, written
 * from here (`vehicleFileSvg`), and a test keeps them equal.
 */

/** The vehicles a rider can ride (Riders' `Rider::VEHICLES`), as the API names them. */
export const VEHICLES = ['motorbike', 'tuk_tuk', 'car'];

/**
 * The vehicle a value names, from the API's key (`tuk_tuk`) or the words a
 * rider card and the session carry ("Tuk-tuk"); null for anything else.
 */
export function vehicleKey(value) {
  const words = String(value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (words === 'tuk_tuk' || words === 'tuktuk') return 'tuk_tuk';
  if (words === 'motorbike' || words === 'motorcycle' || words === 'motor_bike') return 'motorbike';
  if (words === 'car') return 'car';

  return null;
}

/** A vehicle's state on a map, and its ring's colour. */
export const VEHICLE_STATES = {
  free: '#1a8f4a',
  delivering: '#2d74f6',
  silent: '#9ca3af',
};

const BLUE = '#07408f';
const ORANGE = '#ec6c23';
const TYRE = '#1f2937';

/** Each vehicle, on a 48-unit square, nose to the top. */
const DRAWINGS = {
  motorbike: [
    // Wheels, front and back.
    `<rect x="21.5" y="5.5" width="5" height="8" rx="2.5" fill="${TYRE}"/>`,
    `<rect x="21" y="33" width="6" height="9.5" rx="3" fill="${TYRE}"/>`,
    // Frame and handlebars.
    `<rect x="20.5" y="11" width="7" height="22" rx="3.5" fill="#4b5563"/>`,
    `<path d="M14.5 15.5h19" stroke="${TYRE}" stroke-width="2.4" stroke-linecap="round"/>`,
    // The rider: shoulders and helmet, InGo blue.
    `<ellipse cx="24" cy="22.5" rx="7.2" ry="4.2" fill="${BLUE}"/>`,
    `<circle cx="24" cy="20" r="4.4" fill="${BLUE}" stroke="#fff" stroke-width="1.1"/>`,
    // The InGo box behind them.
    `<rect x="17" y="26.5" width="14" height="11.5" rx="2" fill="${ORANGE}" stroke="#fff" stroke-width="1.2"/>`,
    `<path d="M20 30.5h8" stroke="#fff" stroke-width="1.4" stroke-linecap="round"/>`,
  ],
  tuk_tuk: [
    // The front wheel, then the two at the back.
    `<rect x="22.5" y="5.5" width="3" height="6.5" rx="1.5" fill="${TYRE}"/>`,
    `<rect x="12.5" y="30" width="3.5" height="8" rx="1.5" fill="${TYRE}"/>`,
    `<rect x="32" y="30" width="3.5" height="8" rx="1.5" fill="${TYRE}"/>`,
    // The body, narrow at the nose, InGo orange.
    `<path d="M20 10.5Q24 7.5 28 10.5L33.5 35.5Q34 40 30 40H18Q14 40 14.5 35.5Z" fill="${ORANGE}" stroke="#fff" stroke-width="1.2"/>`,
    // The canopy, InGo blue, and the windscreen.
    `<rect x="18" y="19" width="12" height="17" rx="2.5" fill="${BLUE}"/>`,
    `<path d="M19.5 14.5h9l1.5 3.5h-12z" fill="#cfe4f5"/>`,
  ],
  car: [
    // Four wheels.
    `<rect x="13" y="11" width="3.5" height="7" rx="1.5" fill="${TYRE}"/>`,
    `<rect x="31.5" y="11" width="3.5" height="7" rx="1.5" fill="${TYRE}"/>`,
    `<rect x="13" y="30" width="3.5" height="7" rx="1.5" fill="${TYRE}"/>`,
    `<rect x="31.5" y="30" width="3.5" height="7" rx="1.5" fill="${TYRE}"/>`,
    // The body, InGo blue.
    `<rect x="15" y="6" width="18" height="36" rx="7" fill="${BLUE}" stroke="#fff" stroke-width="1.2"/>`,
    // Windscreen and rear window.
    `<path d="M18 15.5Q24 12.5 30 15.5L29 20H19z" fill="#cfe4f5"/>`,
    `<path d="M19 34h10l1 3.5Q24 39.5 18 37.5z" fill="#cfe4f5"/>`,
    // The InGo box on the roof.
    `<rect x="19.5" y="23" width="9" height="8" rx="1.5" fill="${ORANGE}" stroke="#fff" stroke-width="1"/>`,
  ],
  // Four more for the vehicle types the office adds (Rio, 4 Oct 2026: "not
  // limited to the three kinds we have"); a type names one of these drawings.
  bicycle: [
    // Thin wheels, front and back.
    `<rect x="22.5" y="4.5" width="3" height="9" rx="1.5" fill="${TYRE}"/>`,
    `<rect x="22.5" y="33.5" width="3" height="9.5" rx="1.5" fill="${TYRE}"/>`,
    // The frame and the handlebar.
    `<rect x="23" y="11" width="2" height="24" rx="1" fill="#4b5563"/>`,
    `<path d="M16.5 14h15" stroke="${TYRE}" stroke-width="2" stroke-linecap="round"/>`,
    // The rider, InGo blue.
    `<ellipse cx="24" cy="21.5" rx="6.4" ry="3.8" fill="${BLUE}"/>`,
    `<circle cx="24" cy="19.5" r="3.9" fill="${BLUE}" stroke="#fff" stroke-width="1.1"/>`,
    // A small InGo box on the rack.
    `<rect x="18.5" y="27" width="11" height="9" rx="1.8" fill="${ORANGE}" stroke="#fff" stroke-width="1.2"/>`,
  ],
  van: [
    // Four wheels, wider set than the car's.
    `<rect x="11.5" y="9.5" width="4" height="7.5" rx="1.6" fill="${TYRE}"/>`,
    `<rect x="32.5" y="9.5" width="4" height="7.5" rx="1.6" fill="${TYRE}"/>`,
    `<rect x="11.5" y="32" width="4" height="7.5" rx="1.6" fill="${TYRE}"/>`,
    `<rect x="32.5" y="32" width="4" height="7.5" rx="1.6" fill="${TYRE}"/>`,
    // The body, square at the back, InGo blue.
    `<path d="M20 4.5h8Q34.5 4.5 34.5 11V42.5Q34.5 44 33 44H15Q13.5 44 13.5 42.5V11Q13.5 4.5 20 4.5Z" fill="${BLUE}" stroke="#fff" stroke-width="1.2"/>`,
    // The windscreen.
    `<path d="M16.5 12Q24 8.5 31.5 12L30.5 16.5H17.5z" fill="#cfe4f5"/>`,
    // The InGo cargo panel along the roof.
    `<rect x="17" y="20" width="14" height="20" rx="1.8" fill="${ORANGE}" stroke="#fff" stroke-width="1"/>`,
    `<path d="M20 30h8" stroke="#fff" stroke-width="1.4" stroke-linecap="round"/>`,
  ],
  truck: [
    // The cab's wheels, then the body's.
    `<rect x="12" y="8" width="3.5" height="6.5" rx="1.5" fill="${TYRE}"/>`,
    `<rect x="32.5" y="8" width="3.5" height="6.5" rx="1.5" fill="${TYRE}"/>`,
    `<rect x="11" y="28" width="4" height="6.5" rx="1.5" fill="${TYRE}"/>`,
    `<rect x="33" y="28" width="4" height="6.5" rx="1.5" fill="${TYRE}"/>`,
    `<rect x="11" y="36" width="4" height="6.5" rx="1.5" fill="${TYRE}"/>`,
    `<rect x="33" y="36" width="4" height="6.5" rx="1.5" fill="${TYRE}"/>`,
    // The cab, InGo blue, with its windscreen.
    `<rect x="14.5" y="3.5" width="19" height="13" rx="4" fill="${BLUE}" stroke="#fff" stroke-width="1.2"/>`,
    `<path d="M17 8.5Q24 6 31 8.5L30.5 11.5H17.5z" fill="#cfe4f5"/>`,
    // The cargo box, InGo orange, longer than the cab.
    `<rect x="13" y="18" width="22" height="26" rx="2" fill="${ORANGE}" stroke="#fff" stroke-width="1.2"/>`,
    `<path d="M17 31h14" stroke="#fff" stroke-width="1.4" stroke-linecap="round"/>`,
  ],
  parcel: [
    // A plain InGo parcel, for a vehicle none of the others draws.
    `<rect x="11" y="11" width="26" height="26" rx="3" fill="${ORANGE}" stroke="#fff" stroke-width="1.4"/>`,
    // Its tape, InGo blue, and the flap's crease.
    `<rect x="21.5" y="11" width="5" height="26" fill="${BLUE}"/>`,
    `<path d="M11 24h26" stroke="#fff" stroke-width="1.2" stroke-opacity="0.75"/>`,
  ],
};

/** Every drawing InGo has, by name: what a vehicle type the office adds may name. */
export const DRAWINGS_AVAILABLE = Object.keys(DRAWINGS);

/** The file the console copies, in `public/assets/vehicles/`: `tuk_tuk` → `tuk-tuk-top.svg`. */
export function drawingFileName(name) {
  return `${String(name).replace(/_/g, '-')}-top.svg`;
}

function drawingOf(vehicle) {
  return (DRAWINGS[vehicle] ?? DRAWINGS.motorbike).join('');
}

/**
 * The marker a map draws: the vehicle on a white disc ringed in its state's
 * colour, 48 units square, registered at pixel ratio 3 (32 CSS px).
 */
export function vehicleMarkerSvg(vehicle, state = 'free') {
  const ring = VEHICLE_STATES[state] ?? VEHICLE_STATES.free;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 48 48">` +
    `<circle cx="24" cy="24" r="22" fill="#fff" stroke="${ring}" stroke-width="2.5"/>` +
    `<g transform="translate(24 24) scale(0.88) translate(-24 -24)">${drawingOf(vehicle)}</g>` +
    `</svg>`
  );
}

/** The vehicle alone, 44 px, as `public/assets/vehicles/<name>-top.svg` holds it for the console. */
export function vehicleFileSvg(vehicle) {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 48 48">${drawingOf(vehicle)}</svg>\n`;
}

/** The image id a drawing in a state is registered under on a map; anything else is a free motorbike. */
export function vehicleImageId(vehicle, state) {
  return `ingo-vehicle-${DRAWINGS_AVAILABLE.includes(vehicle) ? vehicle : 'motorbike'}-${VEHICLE_STATES[state] ? state : 'free'}`;
}

/** `addImage` takes an `<img>`; MapLibre's own loader cannot decode SVG in Chrome (Kangaru `poiLayer.ts`). */
export function loadSvgImage(svg, size = 96) {
  return new Promise((resolve, reject) => {
    const image = new Image(size, size);
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('a vehicle drawing could not be decoded'));
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
}

/** Registers every drawing in every state on a map, once. */
export async function addVehicleImages(map) {
  const wanted = [];
  for (const vehicle of DRAWINGS_AVAILABLE) {
    for (const state of Object.keys(VEHICLE_STATES)) {
      const id = vehicleImageId(vehicle, state);
      if (!map.hasImage(id)) wanted.push([id, vehicleMarkerSvg(vehicle, state)]);
    }
  }
  const images = await Promise.all(wanted.map(([, svg]) => loadSvgImage(svg)));
  wanted.forEach(([id], i) => {
    if (!map.hasImage(id)) map.addImage(id, images[i], { pixelRatio: 3 });
  });
}
